import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemText,
  Popover,
  InputAdornment,
  SelectChangeEvent,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";

export interface BikeDiscountSelectOption {
  label: string;
  value: string | number;
}

interface BikeDiscountSearchableSelectProps {
  name?: string;
  value: string | number;
  onChange: (event: SelectChangeEvent<string | number>) => void;
  options: BikeDiscountSelectOption[];
  label?: string;
  placeHolder?: string;
  searchPlaceholder?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  loading?: boolean;
  resettable?: boolean;
  backgroundColor?: string;
  searchable?: boolean;
}

const triggerFieldSx = (
  hasError: boolean,
  isInteractionBlocked: boolean,
  backgroundColor: string
) => ({
  cursor: isInteractionBlocked ? "not-allowed" : "pointer",
  borderRadius: "0px",
  height: "40px",
  backgroundColor,
  "& .MuiOutlinedInput-root": {
    borderColor: hasError ? "#d32f2f!important" : "#808080!important",
    borderRadius: "0px",
    height: "40px",
    backgroundColor,
  },
  "& .MuiInputBase-input": {
    color: "#CCCCCC",
    cursor: isInteractionBlocked ? "not-allowed" : "pointer",
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#CCCCCC!important",
    opacity: 1,
  },
  "& fieldset": {
    borderColor: hasError ? "#d32f2f !important" : "#808080",
  },
  "&:hover fieldset": {
    borderColor: hasError ? "#d32f2f !important" : "#808080 !important",
  },
});

const BikeDiscountSearchableSelect: React.FC<
  BikeDiscountSearchableSelectProps
> = ({
  name,
  value,
  onChange,
  options,
  label,
  placeHolder = "Select",
  searchPlaceholder = "Search",
  required,
  error,
  disabled = false,
  loading = false,
  resettable = false,
  backgroundColor = "#393939",
  searchable = true,
}) => {
  const isInteractionBlocked = disabled || loading;
  const [searchTerm, setSearchTerm] = useState("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const selectedLabel =
    options.find((option) => option.value === value)?.label || "";

  useEffect(() => {
    if (!anchorEl) {
      setSearchTerm("");
    }
  }, [anchorEl]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (isInteractionBlocked) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchTerm("");
  };

  const handleSelect = (selectedValue: string | number) => {
    onChange({
      target: { name: name ?? "", value: selectedValue },
    } as SelectChangeEvent<string | number>);
    handleClose();
  };

  const filteredOptions = searchable
    ? options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  const popoverWidth = anchorEl?.clientWidth || 340;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        marginBottom: error ? "0px" : "8px",
      }}
    >
      {label && (
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: "medium",
            fontSize: "12px",
            display: "flex",
            color: "#FFFFFF",
          }}
        >
          {label}{" "}
          {required && (
            <span style={{ color: "red", marginLeft: "5px", lineHeight: "1.5" }}>
              *
            </span>
          )}
        </Typography>
      )}

      <TextField
        fullWidth
        variant="outlined"
        value={selectedLabel}
        placeholder={placeHolder}
        onClick={handleOpen}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end" sx={{ gap: 0.5 }}>
              {loading && (
                <CircularProgress size={16} sx={{ color: "#CCCCCC" }} />
              )}
              <ExpandMoreIcon sx={{ color: "#CCCCCC" }} />
            </InputAdornment>
          ),
        }}
        sx={triggerFieldSx(!!error, isInteractionBlocked, backgroundColor)}
      />

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableScrollLock
        sx={{ mt: 0.5 }}
      >
        <Box
          sx={{
            width: popoverWidth,
            borderRadius: "0px",
            background: "#212121",
          }}
        >
          <Box p={searchable ? 1 : 0} sx={{ background: backgroundColor }}>
            {searchable && (
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
              sx={{
                borderRadius: "0px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "0px",
                  height: "40px",
                  backgroundColor,
                },
                "& input": {
                  color: "#CCCCCC !important",
                },
                "& input::placeholder": {
                  color: "#CCCCCC",
                  opacity: 1,
                },
                "& fieldset": {
                  borderColor: "#808080",
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon fontSize="small" sx={{ color: "#CCCCCC" }} />
                  </InputAdornment>
                ),
              }}
            />
            )}
          </Box>

          <List
            sx={{
              maxHeight: 220,
              overflowY: "auto",
              p: 1,
              background: backgroundColor,
              m: 0,
            }}
          >
            {resettable && (
              <ListItem
                onClick={() => handleSelect("")}
                sx={{
                  border: "1px solid #808080",
                  mb: 0.5,
                  bgcolor: backgroundColor,
                  color: "#808080",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "#4A4A4A",
                  },
                }}
              >
                <ListItemText
                  primary={placeHolder}
                  primaryTypographyProps={{
                    fontSize: "14px",
                    color: "#808080",
                  }}
                />
              </ListItem>
            )}
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <ListItem
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  sx={{
                    border: "1px solid #808080",
                    mb: 0.5,
                    bgcolor: backgroundColor,
                    color: "#CCCCCC",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "#4A4A4A",
                    },
                    "&:last-child": {
                      mb: 0,
                    },
                  }}
                >
                  <ListItemText
                    primary={option.label}
                    primaryTypographyProps={{
                      fontSize: "14px",
                      color: "#CCCCCC",
                    }}
                  />
                </ListItem>
              ))
            ) : (
              <ListItem sx={{ color: "#CCCCCC", justifyContent: "center" }}>
                <ListItemText
                  primary="No results found"
                  primaryTypographyProps={{
                    fontSize: "13px",
                    color: "#CCCCCC",
                    textAlign: "center",
                  }}
                />
              </ListItem>
            )}
          </List>
        </Box>
      </Popover>

      {error && (
        <Typography
          className="f-12"
          sx={{
            fontWeight: "medium",
            textAlign: "left",
            color: "#d32f2f",
            paddingTop: "0px",
          }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default BikeDiscountSearchableSelect;
